import capitalize from "lodash-es/capitalize";
import clamp from "lodash-es/clamp";

import {
  Maybe,
  RunWorkerMetricsDiagnostic,
  RunWorkerMetricsDiagnosticSeverity,
  RunWorkerMetricsDiagnosticSnippet,
  RunWorkerMetricsStateError,
} from "types/generated";

export type RunObservabilityErrorPart = {
  text: string;
  style?: RunWorkerMetricsDiagnosticSeverity | "strong" | "highlight" | "muted";
};

type RunObservabilityErrorLine = RunObservabilityErrorPart[];

const DATA_SOURCE_ADDRESS_PATTERN = /^(module\.[^.[]+(\[[^\]]*\])?\.)*data\./;

export const getRunObservabilityErrorTitle = (resourceAddress: Maybe<string>) => {
  if (!resourceAddress) {
    return "Error";
  }

  return DATA_SOURCE_ADDRESS_PATTERN.test(resourceAddress)
    ? "Data source failed"
    : "Resource failed";
};

const getSnippetCodeLines = ({
  code,
  startLine,
  highlightStartOffset,
  highlightEndOffset,
}: RunWorkerMetricsDiagnosticSnippet): RunObservabilityErrorLine[] => {
  const encoder = new TextEncoder();
  const decoder = new TextDecoder();
  let lineOffset = 0;

  return code.split("\n").map((line, index) => {
    const bytes = encoder.encode(line);
    const start = clamp(highlightStartOffset - lineOffset, 0, bytes.length);
    const end = clamp(highlightEndOffset - lineOffset, start, bytes.length);

    lineOffset += bytes.length + 1;

    const parts: RunObservabilityErrorLine = [
      {
        text: `${String(startLine + index).padStart(4)}: ${decoder.decode(bytes.slice(0, start))}`,
      },
      { text: decoder.decode(bytes.slice(start, end)), style: "highlight" },
      { text: decoder.decode(bytes.slice(end)) },
    ];

    return parts.filter(({ text }) => text);
  });
};

const getSnippetValueLines = ({
  values,
}: RunWorkerMetricsDiagnosticSnippet): RunObservabilityErrorLine[] =>
  values.length > 0
    ? [
        [{ text: "    ├────────────────", style: "muted" }],
        ...values.map(
          ({ traversal, statement }): RunObservabilityErrorLine => [
            { text: "    │", style: "muted" },
            { text: " " },
            { text: traversal, style: "strong" },
            { text: ` ${statement}` },
          ]
        ),
      ]
    : [];

const getSourceLines = (
  { range, snippet }: RunWorkerMetricsDiagnostic,
  resourceAddress: Maybe<string>
): RunObservabilityErrorLine[] => {
  const lines: RunObservabilityErrorLine[] = [];

  if (resourceAddress) {
    lines.push([{ text: `  with ${resourceAddress},` }]);
  }

  if (range) {
    const context = snippet?.context ? `, in ${snippet.context}` : "";

    lines.push([
      { text: `  on ${range.filename} line ${range.start.line}${context}${snippet ? ":" : ""}` },
    ]);
  }

  if (range && snippet) {
    lines.push(...getSnippetCodeLines(snippet), ...getSnippetValueLines(snippet));
  }

  return lines;
};

const getHeadingLine = (
  severity: RunWorkerMetricsDiagnosticSeverity,
  summary: string
): RunObservabilityErrorLine => [
  { text: `${capitalize(severity)}:`, style: severity },
  { text: " " },
  { text: summary, style: "strong" },
];

const getDiagnosticLines = (
  diagnostic: RunWorkerMetricsDiagnostic,
  resourceAddress: Maybe<string>
): RunObservabilityErrorLine[] => {
  const { severity, summary, detail } = diagnostic;
  const sourceLines = getSourceLines(diagnostic, resourceAddress);
  const lines = [getHeadingLine(severity, summary)];

  if (sourceLines.length > 0) {
    lines.push([], ...sourceLines);
  }

  if (detail) {
    lines.push([], ...detail.split("\n").map((text) => [{ text }]));
  }

  return lines;
};

const getMessageLines = (message: string): RunObservabilityErrorLine[] => {
  const [heading, ...rest] = message.split("\n");
  const restLines = rest.map((text): RunObservabilityErrorLine => [{ text }]);
  const severity = Object.values(RunWorkerMetricsDiagnosticSeverity).find((value) =>
    heading.startsWith(`${capitalize(value)}: `)
  );

  if (!severity) {
    return [[{ text: heading }], ...restLines];
  }

  return [
    getHeadingLine(severity, heading.slice(`${capitalize(severity)}: `.length)),
    ...restLines,
  ];
};

export const getRunObservabilityErrorLines = ({
  message,
  resourceAddress,
  diagnostic,
}: RunWorkerMetricsStateError): RunObservabilityErrorLine[] =>
  diagnostic ? getDiagnosticLines(diagnostic, resourceAddress) : getMessageLines(message);

export const getRunObservabilityErrorText = (lines: RunObservabilityErrorLine[]) =>
  lines.map((line) => line.map(({ text }) => text).join("")).join("\n");
